import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import CommonTable from '../../components/commonComponent/CommanTable';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const CompanyTypePage = () => {
  const [companyTypes, setCompanyTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [formValues, setFormValues] = useState({
    name: '',
  });

  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCompanyTypes();
  }, []);

  /* ================= FETCH ================= */

  const fetchCompanyTypes = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/admin/api/businessType');
      setCompanyTypes(res.data.businessTypes || res.data.businessType || res.data || []);
    } catch (err) {
      toastError('Failed to fetch company types');
    } finally {
      setLoading(false);
    }
  };

  /* ================= SAVE ================= */

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formValues.name,
      };

      if (editingId) {
        await axios.put(`/admin/api/businessType/${editingId}`, payload);
        toastSuccess('Company Type updated successfully!');
      } else {
        await axios.post('/admin/api/businessType', payload);
        toastSuccess('Company Type added successfully!');
      }

      fetchCompanyTypes();
      handleCancel();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save company type');
    } finally {
      setSaving(false);
    }
  };

  /* ================= ACTIONS ================= */

  const handleEdit = (item) => {
    setFormValues({ name: item.name || '' });
    setEditingId(item._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const res = await confirmDelete('Are you sure you want to delete this company type?');
    if (!res.isConfirmed) return;

    try {
      await axios.delete(`/admin/api/businessType/${id}`);
      toastSuccess('Company Type deleted successfully!');
      fetchCompanyTypes();
    } catch (err) {
      toastError('Failed to delete company type');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '' });
    setEditingId(null);
    setErrors({});
  };

  /* ================= TABLE ================= */

  const tableColumns = [
    { key: 'srNo', label: 'Sr No.' },
    { key: 'name', label: 'Company Type Name' },
    { key: 'actions', label: 'Actions' },
  ];

  const filteredData = companyTypes.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const tableData = filteredData.map((item, index) => ({
    srNo: index + 1,
    name: item.name,
    actions: (
      <div>
        <button
          className="btn btn-sm me-1 border-0 text-primary"
          onClick={() => handleEdit(item)}
        >
          <FaEdit size={14} />
        </button>
      </div>
    ),
  }));

  /* ================= UI ================= */

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <h3 className="mb-0 mt-4">Company Types</h3>

        <div className="d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search Company Type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: 250 }}
          />
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Add Company Type
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" />
        </div>
      ) : (
        <CommonTable columns={tableColumns} data={tableData} />
      )}

      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit Company Type' : 'Add Company Type'}
        fields={[
          {
            name: 'name',
            label: 'Company Type Name',
            type: 'text',
            placeholder: 'Enter Company Type',
          },
        ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
        requiredFields={['name']}
        disabled={saving}
      />
    </div>
  );
};

export default CompanyTypePage;
